/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { IUser } from '../interfaces/IUser';

// Words that should render as a fixed acronym rather than plain Title Case.
const ACRONYMS: Record<string, string> = {
  eccn: 'ECCN',
  ccl: 'CCL',
  itar: 'ITAR',
  nrc: 'NRC',
};

const titleCaseWords = (words: string[]): string =>
  words
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const PAGE_VIEW_PREFIX = 'page_view:';

/**
 * Converts a raw action key into the human-readable label actually stored in
 * AppActions.action, e.g.:
 *   'entity_screening:view_adverse_media' -> 'Entity Screening View Adverse Media'
 *   'page_view:/bite-classify'            -> 'Navigated to Bite Classify'
 *   'page_view:/'                         -> 'Home Page'
 * Call sites keep writing short, greppable `tool:verb_phrase` keys; this is
 * the one place that turns them into what actually lands in the DB/OpenSearch.
 */
const formatActionLabel = (action: string): string => {
  if (action.startsWith(PAGE_VIEW_PREFIX)) {
    const path = action.slice(PAGE_VIEW_PREFIX.length);
    if (path === '/') return 'Home Page';
    return `Navigated to ${titleCaseWords(path.split(/[/_-]+/))}`;
  }
  return titleCaseWords(action.split(/[:_\s]+/));
};

/**
 * Usage telemetry: page/tool navigation + significant button clicks.
 * Fire-and-forget — never blocks the UI and never surfaces an error, since a
 * failed log write shouldn't interrupt the action the user was taking.
 * Backed by POST /api/v1/app-actions -> public."AppActions" (init.sql).
 */
export const AppActionsAPI = {
  logAction: async (
    action: string,
    cur_user: IUser | undefined,
    domain?: string,
    isWhiteLabel?: boolean,
    tenantName?: string,
  ): Promise<void> => {
    const userId = cur_user?.basics?.id;
    if (!userId) return; // pre-auth / not yet hydrated — nothing to attribute the event to

    try {
      const client = await initAxios();
      await client?.post('/app-actions', {
        action: formatActionLabel(action),
        userId: userId,
        username: cur_user?.basics?.username,
        userFirstName: cur_user?.basics?.firstName,
        userLastName: cur_user?.basics?.lastName,
        groupId: cur_user?.selected_team?.id,
        teamName: cur_user?.selected_team?.name,
        domain: domain,
        isWhiteLabel: isWhiteLabel,
        tenantName: tenantName,
      });
    } catch {
      // Swallow — telemetry failures are not user-facing.
    }
  },
  // Super-admin only -- powers the "Last Accessed" column on
  // components/SuperAdmin/ActiveTeams.tsx. Returns { [team_name]: ISO date }.
  getLastAccessedByTeam: async (): Promise<Record<string, string>> => {
    try {
      const client = await initAxios();
      const resp = await client?.get('/app-actions/last-accessed');
      return resp?.data ?? {};
    } catch {
      return {};
    }
  },
  // Super-admin only -- starts the AppActions -> OpenSearch sync in the
  // background. Resolves with the HTTP status (202 started, 409 already running).
  triggerSync: async (): Promise<number> => {
    try {
      const client = await initAxios();
      const resp = await client?.post('/app-actions/sync');
      return resp?.status ?? 500;
    } catch (err: any) {
      return err?.response?.status ?? 500;
    }
  },
};

export default AppActionsAPI;
